import CreditsCard from "@/components/dashboard/CreditsCard";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Sparkles } from "lucide-react";

// Dedicated /dashboard/credits page. The heavy lifting (balance, buy
// modal, checkout redirect) lives inside CreditsCard, which is the
// same component used on /dashboard and /dashboard/settings — no
// duplicated logic, no new Stripe SKU, no new checkout path.
export default function CreditsPage() {
  const { user } = useAuth();
  const tier = user?.tier || {};
  const limit = tier.ai_credits_limit;
  const isUnlimited = limit === null || limit === undefined;
  const planLabel = tier.plan_key || user?.subscription_plan || "Presale";

  // Credits are Kickstart-only. Redirect any non-Kickstart caller back
  // to the dashboard so subscription / trial / founder users can never
  // land on this page (2026-08-30).
  if (!String(user?.subscription_plan || "").startsWith("Kickstart")) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="max-w-3xl" data-testid="credits-page">
      <p className="zy-eyebrow mb-2">Credits</p>
      <h1 className="text-[28px] font-bold tracking-tight flex items-center gap-2.5">
        <Sparkles size={22} style={{ color: "#202B60" }} />
        AI credits
      </h1>
      <p className="text-[14px] text-[#555] mt-1">
        Credits fuel Zyntha, Thoro, Zyona and every assistant across your
        workspace. One credit = one AI reply or generation.
      </p>

      <section className="mt-8">
        <CreditsCard />
      </section>

      <section className="mt-6 grid gap-3 sm:grid-cols-3">
        {[
          { key: "Kickstart 1", credits: 50 },
          { key: "Kickstart 2", credits: 150 },
          { key: "Kickstart 3", credits: 300 },
        ].map((row) => {
          const active = String(planLabel).startsWith(row.key);
          return (
            <div
              key={row.key}
              data-testid={`kickstart-allotment-${row.key.split(" ")[1]}`}
              className={`rounded-xl border p-4 ${active ? "border-[#202B60] bg-[#EAF0FF]" : "border-[#eee] bg-white"}`}
            >
              <p className="text-[11px] uppercase tracking-wider font-semibold text-[#666] notranslate" translate="no">
                {row.key}
              </p>
              <p className="mt-1.5 text-[20px] font-bold text-[#0A1628]">
                {row.credits} <span className="text-[13px] font-medium text-[#666]">included</span>
              </p>
              {active && (
                <p className="mt-1 text-[11.5px] font-semibold text-[#202B60]">
                  Your plan
                </p>
              )}
            </div>
          );
        })}
      </section>

      <p className="mt-6 text-[12.5px] text-[#666]">
        Kickstart credits come with your lifetime licence. Need more?
        Use the Buy credits button above — top-ups are added instantly on payment.
      </p>
    </div>
  );
}
